import React, { useEffect, useState } from 'react';
import { Loader2, UserPlus } from 'lucide-react';
import { agentService } from '@/services/agentService';
import { authService } from '@/services/authService';
import { Card, CardContent } from '@/components/ui/card';
import type { Agent } from '@/types/domain';
import { cn } from '@/lib/utils';
import { AddAgentDialog } from './AddAgentDialog';

/**
 * Trade Portal access per agent: the email their sign-in link goes to, and whether they may
 * sign in. An agent needs both before a Photo Quote can be sent to them. Admins edit; everyone
 * else sees the list read-only (the API refuses non-admin changes).
 */
const AgentPortalAccess: React.FC = () => {
  const isAdmin = authService.getCachedAgent()?.is_admin === true;
  const [agents, setAgents] = useState<Agent[]>([]);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  function addCreated(agent: Agent) {
    setAgents((current) => [...current, agent].sort((a, b) => a.name.localeCompare(b.name)));
    setEmails((current) => ({ ...current, [agent.id]: agent.email ?? '' }));
  }

  useEffect(() => {
    agentService
      .list()
      // 'trade-portal' is the system agent portal orders are filed under, not a person.
      .then((rows) => {
        const people = rows.filter((row) => row.id !== 'trade-portal');
        setAgents(people);
        setEmails(Object.fromEntries(people.map((row) => [row.id, row.email ?? ''])));
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load agents'));
  }, []);

  async function save(agent: Agent, patch: { email?: string | null; portal_access?: boolean }) {
    setSavingId(agent.id);
    setError(null);
    try {
      const updated = await agentService.update(agent.id, patch);
      setAgents((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      setEmails((current) => ({ ...current, [updated.id]: updated.email ?? '' }));
    } catch (err) {
      setError(`${agent.name}: ${err instanceof Error ? err.message : 'Update failed'}`);
      // Put the field back to what is stored.
      setEmails((current) => ({ ...current, [agent.id]: agent.email ?? '' }));
    } finally {
      setSavingId(null);
    }
  }

  function saveEmail(agent: Agent) {
    const next = (emails[agent.id] ?? '').trim().toLowerCase();
    if (next === (agent.email ?? '')) return;
    void save(agent, { email: next || null });
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium text-muted-foreground">Trade Portal access</h3>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium hover:bg-accent"
            >
              <UserPlus className="size-3.5" />
              Add agent
            </button>
          )}
        </div>
        {adding && <AddAgentDialog onCreated={addCreated} onClose={() => setAdding(false)} />}
        <p className="mb-3 text-xs text-muted-foreground">
          Agents sign in to the Trade Portal with a link sent to this email. Photo Quotes can be sent
          to agents with an email and access turned on.
        </p>

        {error && (
          <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="pb-2 font-medium text-muted-foreground">Agent</th>
                <th className="pb-2 font-medium text-muted-foreground">Sign-in email</th>
                <th className="pb-2 text-center font-medium text-muted-foreground w-32">Portal access</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => {
                const busy = savingId === agent.id;
                // Access needs an email to sign in with; turning it off is always allowed.
                const canToggle = isAdmin && !busy && (agent.portal_access || Boolean(agent.email));
                return (
                  <tr key={agent.id} className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-4">
                      <span className="font-medium">{agent.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {agent.is_admin ? 'Admin' : 'Sales agent'}
                      </span>
                    </td>
                    <td className="py-2 pr-4">
                      {isAdmin ? (
                        <input
                          type="email"
                          value={emails[agent.id] ?? ''}
                          placeholder="name@example.com"
                          disabled={busy}
                          onChange={(e) =>
                            setEmails((current) => ({ ...current, [agent.id]: e.target.value }))
                          }
                          onBlur={() => saveEmail(agent)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                          aria-label={`Sign-in email for ${agent.name}`}
                          className="h-8 w-full min-w-[200px] rounded-md border border-input bg-transparent px-2 text-sm shadow-xs disabled:opacity-60"
                        />
                      ) : (
                        <span className="text-muted-foreground">{agent.email ?? '—'}</span>
                      )}
                    </td>
                    <td className="py-2 text-center">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={agent.portal_access}
                          aria-label={`Trade Portal access for ${agent.name}`}
                          disabled={!canToggle}
                          title={!agent.email && !agent.portal_access ? 'Add an email first' : undefined}
                          onClick={() => void save(agent, { portal_access: !agent.portal_access })}
                          className={cn(
                            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                            agent.portal_access ? 'bg-primary' : 'bg-muted',
                          )}
                        >
                          <span
                            className={cn(
                              'inline-block size-4 rounded-full bg-background shadow transition-transform',
                              agent.portal_access ? 'translate-x-4' : 'translate-x-0.5',
                            )}
                          />
                        </button>
                        {busy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentPortalAccess;
